import React, {useCallback} from 'react'
import {Dictionary} from 'lodash'
import {RouteComponentProps, Link} from '@reach/router'
import {useMutation} from '@apollo/client'
import {Formik, FormikProps, FormikConfig, FormikHelpers} from 'formik'
import {LockFill, PersonLinesFill, CheckCircle} from 'react-bootstrap-icons'

import {Page, Container, Loader} from '../layout'
import {Field} from '../form/fields'
import {accountRegisterMutation} from './queries'
import {getAbsoluteUrl} from '../../utils/url'
import {
  AccountRegister,
  AccountRegisterVariables,
  AccountRegister_accountRegister_user,
} from './_gql-types/AccountRegister'


interface FormValues {
  email: string
  password: string
  password2: string
}

interface FormErrors {
  email?: string
  password?: string
  password2?: string
}

const reqMsg = 'Þennan reit þarf að fylla út'

// eslint-disable-next-line
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

function validate({
  email,
  password,
  password2,
}: FormValues) {
  const obj: FormErrors = {}

  if (!email) {
    obj.email = reqMsg
  }
  else if (!emailRegex.test(email)) {
    obj.email = 'Þetta er ekki gilt tölvupóstfang'
  }

  if (!password) {
    obj.password = reqMsg
  }
  else if (password.length < 8) {
    obj.password = 'Þetta lykilorð er of stutt'
  }

  if (!password2) {
    obj.password2 = reqMsg
  }
  else if (password !== password2) {
    obj.password2 = 'Lykilorðin passa ekki saman'
  }

  return obj
}

const fieldErrorCodeMap: Dictionary<string> = {
  // ACTIVATE_OWN_ACCOUNT: '',
  // ACTIVATE_SUPERUSER_ACCOUNT: '',
  // DEACTIVATE_OWN_ACCOUNT: '',
  // DEACTIVATE_SUPERUSER_ACCOUNT: '',
  // DELETE_NON_STAFF_USER: '',
  // DELETE_OWN_ACCOUNT: '',
  // DELETE_STAFF_ACCOUNT: '',
  // DELETE_SUPERUSER_ACCOUNT: '',
  // DUPLICATED_INPUT_ITEM: '',
  // GRAPHQL_ERROR: '',
  // INVALID: '',
  // INVALID_CREDENTIALS: '',
  // INVALID_PASSWORD: '',
  // LEFT_NOT_MANAGEABLE_PERMISSION: '',
  // NOT_FOUND: '',
  // OUT_OF_SCOPE_GROUP: '',
  // OUT_OF_SCOPE_PERMISSION: '',
  // OUT_OF_SCOPE_SERVICE_ACCOUNT: '',
  // OUT_OF_SCOPE_USER: '',
  PASSWORD_ENTIRELY_NUMERIC: 'Þetta lykilorð er of einfalt',
  PASSWORD_TOO_COMMON: 'Þetta lykilorð er of algengt',
  PASSWORD_TOO_SHORT: 'Þetta lykilorð er of stutt',
  PASSWORD_TOO_SIMILAR: 'Þetta lykilorð er ekki gilt',
  // REQUIRED: '',
  UNIQUE: 'Notandi með þetta tölvupóstfang er núþegar til',
}

export default function Registration(_: RouteComponentProps) {
  const [register, {data, loading}] = useMutation<AccountRegister, AccountRegisterVariables>(accountRegisterMutation)

  const onSubmit = useCallback(async (
    {email, password}: FormValues,
    {setFieldError}: FormikHelpers<FormValues>,
  ) => {
    const {data: _data} = await register({variables: {
      email,
      password,
      redirectUrl: getAbsoluteUrl('/innskraning/'),
    }})

    _data?.accountRegister?.accountErrors.forEach((err) => {
      if (err.field) {
        const msg = fieldErrorCodeMap[err.code]
        if (msg == null) {
          // TODO: Log to sentry
          console.error(`Got an undefined error code (${err.code}) for field: ${err.field}`)
        }
        setFieldError(err.field as keyof FormValues, msg ?? `Óþekkt villa (${err.code})`)
      }
    })
  }, [register])

  const formikProps: FormikConfig<FormValues> = {
    initialValues: {
      email: '',
      password: '',
      password2: '',
    },
    onSubmit,
    validate,
  }

  const user = data?.accountRegister?.user

  return (
    <Page>
      {loading && <Loader />}

      <Container size="lg">
        <div className="row justify-content-center pt-5">
          <div className="col-sm-8 col-xl-5">
            <div className="p-5 bg-white rounded border border-gray">
              {!user ? (
                <Formik component={RegistrationForm} {...formikProps} />
              ) : (
                <RegistrationSuccess user={user} />
              )}
            </div>
          </div>
        </div>

        <div className="row justify-content-center py-4">
          <div className="col-sm-8 col-xl-5">
            <div className="px-5 py-4 bg-white rounded border border-gray d-flex align-items-center justify-content-between">
              <span className="text-muted">Með aðgang?</span>
              <Link className="text-secondary" to="/innskraning/">
                <LockFill size={18} className="mr-2" />
                Innskráning
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </Page>
  )
}

function RegistrationForm({handleSubmit}: FormikProps<FormValues>) {
  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex justify-content-between mb-5">
        <h1 className="h2">Nýskráning</h1>
        <PersonLinesFill size={30} className="text-dark" />
      </div>

      <div className="mb-4">
        <Field name="email" type="text" label="Netfang" className="mb-3" />
        <Field name="password" type="password" label="Lykilorð" className="mb-3" autoComplete="off" />
        <Field name="password2" type="password" label="Lykilorð (aftur)" className="mb-3" autoComplete="off" />
      </div>

      <button className="btn btn-primary w-100" type="submit">Nýskrá</button>
    </form>
  )
}

interface RegistrationSuccessProps {
  user: AccountRegister_accountRegister_user
}

function RegistrationSuccess({user}: RegistrationSuccessProps) {
  return (
    <div>
      <div className="d-flex justify-content-between mb-5">
        <h2 className="text-success">Skráning tókst!</h2>
        <CheckCircle size={30} className="text-success" />
      </div>
      <p className="text-muted">
        Innan skamms færðu tölvupóst á netfangið <span className="badge bg-light text-dark">{user.email}</span> með hlekk til að virkja aðganginn þinn
      </p>
    </div>
  )
}
